// Optimized camelCase implementation using character iteration
const camelCase = (str) => {
    const len = str.length;
    if (len === 0)
        return str;
    const firstCharCode = str.charCodeAt(0);
    // Fast path: check if already camelCase (starts with lowercase, no separators, no digit-lowercase transitions)
    // Combined check to avoid second loop
    let hasSeparators = false;
    const startsWithLowercase = firstCharCode >= 97 && firstCharCode <= 122;
    if (startsWithLowercase) {
        // Quick check for separators and digit-lowercase transitions
        let prevWasDigit = false;
        for (let i = 1; i < len; i++) {
            const code = str.charCodeAt(i);
            const isDigit = code >= 48 && code <= 57;
            const isLower = code >= 97 && code <= 122;
            const isUpper = code >= 65 && code <= 90;
            const isAlphaNumeric = isDigit || isUpper || isLower;
            if (!isAlphaNumeric) {
                hasSeparators = true;
                break;
            }
            // Check for digit followed by lowercase (needs transformation)
            if (prevWasDigit && isLower) {
                hasSeparators = true;
                break;
            }
            prevWasDigit = isDigit;
        }
        if (!hasSeparators)
            return str; // Already camelCase
    }
    else {
        // Check for separators starting from beginning
        for (let i = 0; i < len; i++) {
            const code = str.charCodeAt(i);
            const isAlphaNumeric = (code >= 48 && code <= 57) || (code >= 65 && code <= 90) || (code >= 97 && code <= 122);
            if (!isAlphaNumeric) {
                hasSeparators = true;
                break;
            }
        }
    }
    let result = '';
    let capitalizeNext = false;
    let prevWasDigit = false;
    let prevWasLower = false;
    let prevWasUpper = false;
    for (let i = 0; i < len; i++) {
        const code = str.charCodeAt(i);
        // Check if alphanumeric (0-9: 48-57, A-Z: 65-90, a-z: 97-122)
        const isDigit = code >= 48 && code <= 57;
        const isUpper = code >= 65 && code <= 90;
        const isLower = code >= 97 && code <= 122;
        if (isDigit || isUpper || isLower) {
            if (capitalizeNext || (prevWasDigit && isLower)) {
                // First character after separator or lowercase after digit - should be uppercase
                result += isLower ? String.fromCharCode(code - 32) : str[i];
                capitalizeNext = false;
                prevWasLower = isLower;
                prevWasUpper = isUpper;
            }
            else if (isUpper) {
                // Check if this uppercase letter should remain uppercase
                // 1. If it follows a lowercase letter (word boundary in PascalCase/camelCase)
                // 2. If it's part of consecutive uppercase letters AND the next char is lowercase (e.g., 'I' in 'MyAPIResponse')
                const nextCode = i + 1 < len ? str.charCodeAt(i + 1) : 0;
                const nextIsLower = nextCode >= 97 && nextCode <= 122;
                if (prevWasLower || (prevWasUpper && nextIsLower)) {
                    // This is a new word - keep uppercase
                    result += str[i];
                    prevWasLower = false;
                    prevWasUpper = true;
                }
                else {
                    // Uppercase letter - lowercase it
                    result += String.fromCharCode(code + 32);
                    prevWasLower = false;
                    prevWasUpper = true;
                }
            }
            else {
                // Keep as is (lowercase or digit)
                result += str[i];
                prevWasLower = isLower;
                prevWasUpper = false;
            }
            prevWasDigit = isDigit;
        }
        else {
            // Non-alphanumeric character - capitalize next letter
            capitalizeNext = result.length > 0;
            prevWasDigit = false;
            prevWasLower = false;
            prevWasUpper = false;
        }
    }
    return result;
};
// Optimized snakeCase implementation using character iteration
const snakeCase = (str) => {
    const len = str.length;
    let result = '';
    let prevWasUpper = false;
    let prevWasLower = false;
    let prevWasDigit = false;
    for (let i = 0; i < len; i++) {
        const char = str[i];
        const code = str.charCodeAt(i);
        // A-Z: 65-90, a-z: 97-122, 0-9: 48-57
        const isUpper = code >= 65 && code <= 90;
        const isLower = code >= 97 && code <= 122;
        const isDigit = code >= 48 && code <= 57;
        const isAlphaNumeric = isUpper || isLower || isDigit;
        if (isAlphaNumeric) {
            // Add underscore before uppercase if preceded by lowercase, digit, or if part of consecutive capitals followed by lowercase
            if (isUpper && (prevWasLower || prevWasDigit || (prevWasUpper && i + 1 < len && str.charCodeAt(i + 1) >= 97 && str.charCodeAt(i + 1) <= 122))) {
                if (result.length > 0)
                    result += '_';
                result += String.fromCharCode(code + 32); // Convert to lowercase
                prevWasUpper = true;
                prevWasLower = false;
                prevWasDigit = false;
            }
            // Add underscore before digit if preceded by letter
            else if (isDigit && (prevWasLower || prevWasUpper)) {
                if (result.length > 0)
                    result += '_';
                result += char;
                prevWasUpper = false;
                prevWasLower = false;
                prevWasDigit = true;
            }
            // Regular uppercase
            else if (isUpper) {
                result += String.fromCharCode(code + 32); // Convert to lowercase
                prevWasUpper = true;
                prevWasLower = false;
                prevWasDigit = false;
            }
            // Regular lowercase or digit
            else {
                result += char;
                prevWasUpper = false;
                prevWasLower = isLower;
                prevWasDigit = isDigit;
            }
        }
        else {
            // Non-alphanumeric - add underscore if we have content
            if (result.length > 0 && result[result.length - 1] !== '_') {
                result += '_';
            }
            prevWasUpper = false;
            prevWasLower = false;
            prevWasDigit = false;
        }
    }
    // Remove trailing underscores
    let endIndex = result.length;
    while (endIndex > 0 && result[endIndex - 1] === '_') {
        endIndex--;
    }
    if (endIndex < result.length) {
        result = result.slice(0, endIndex);
    }
    return result;
};
export const camelKeys = (data) => {
    return changeCase(data, camelCase);
};
export const snakeKeys = (data) => {
    return changeCase(data, snakeCase);
};
const changeCase = (data, transform) => {
    // Fast path for primitives and special objects
    if (data === null || data === undefined || typeof data !== 'object' || data instanceof Date) {
        return data;
    }
    // Fast path for arrays - use for loop instead of map for better performance
    if (Array.isArray(data)) {
        const len = data.length;
        const result = new Array(len);
        for (let i = 0; i < len; i++) {
            result[i] = changeCase(data[i], transform);
        }
        return result;
    }
    // Fast path for objects - use direct property iteration instead of reduce
    const obj = data;
    const result = {};
    const keys = Object.keys(obj);
    const len = keys.length;
    for (let i = 0; i < len; i++) {
        const key = keys[i];
        // Skip transformation for 'details' field
        if (key === "details") {
            result[key] = obj[key];
        }
        else {
            result[transform(key)] = changeCase(obj[key], transform);
        }
    }
    return result;
};
